/**
 * 等级值到 Hz 的协议编码表：下标即协议里的回报率等级（0-6）。
 * 这是编解码依据，不是「设备支持哪些档」的列表，不可增删。
 */
export const POLLING_RATE_LEVELS_HZ = [
  125,
  250,
  500,
  1000,
  2000,
  4000,
  8000,
];

/**
 * V4.0 以下协议的 type3 不带回报率表，无法读出设备实际支持的档位，
 * 只能用这份固定列表兜底。已知的 4.0 以下机型都不支持 250Hz，
 * 因此这里排除等级 1。V4.0 走 coredRPTTABLE/wirelessRPTTABLE 实报，
 * 不受本列表影响。
 */
export const LEGACY_SUPPORTED_REPORT_RATES_HZ = [
  125,
  500,
  1000,
  2000,
  4000,
  8000,
];

export const pollingRateLevelToHz = (level) =>
  POLLING_RATE_LEVELS_HZ[Number(level)] ?? null;

export const pollingRateHzToLevel = (hz) => {
  const index = POLLING_RATE_LEVELS_HZ.indexOf(Number(hz));
  return index >= 0 ? index : null;
};

/**
 * byte8 / byte17：bit7=1 用自定义 Hz，bit0-6 仍是 0-6 档位。
 * 自定义 Hz 在 payload 52-55（完整包 56-59）。
 */
export const REPORT_RATE_CUSTOM_BIT = 0x80;
export const REPORT_RATE_LEVEL_MASK = 0x7f;
export const CUSTOM_REPORT_RATE_RANGE = Object.freeze({ min: 125, max: 8000 });

export const decodeReportRateByte = (byte) => {
  const value = Number(byte) & 0xff;
  if (!Number.isFinite(Number(byte))) {
    return { custom: false, level: 3 };
  }
  return {
    custom: (value & REPORT_RATE_CUSTOM_BIT) !== 0,
    level: Math.min(6, value & REPORT_RATE_LEVEL_MASK),
  };
};

export const encodeReportRateByte = (
  { custom, level } = {},
  previous = 0
) => {
  const prev = decodeReportRateByte(previous);
  const nextLevel = Number.isFinite(Number(level))
    ? Math.max(0, Math.min(6, Math.round(Number(level))))
    : prev.level;
  return ((custom ? REPORT_RATE_CUSTOM_BIT : 0) | nextLevel) & 0xff;
};

export const clampCustomReportRateHz = (hz, fallback = 1000) => {
  if (String(hz ?? '').trim() === '') return fallback;
  const number = Number(hz);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(
    CUSTOM_REPORT_RATE_RANGE.min,
    Math.min(CUSTOM_REPORT_RATE_RANGE.max, Math.round(number))
  );
};

/** bit7=1 时用自定义 Hz；否则用档位表。自定义值无效时退回档位 */
export const resolveReportRateHz = (byte, customHz) => {
  const { custom, level } = decodeReportRateByte(byte);
  if (custom) {
    const hz = Number(customHz);
    if (Number.isFinite(hz) && hz > 0) return Math.round(hz);
  }
  return pollingRateLevelToHz(level);
};

/**
 * 设备返回的回报率表存放支持的等级值（0-6）。
 * 无效值（例如未支持档位常用的 0xff）以及重复值不会进入展示列表。
 */
export const pollingRateTableToHz = (table) => {
  if (!Array.isArray(table) && !(table instanceof Uint8Array)) return [];

  return Array.from(
    new Set(
      Array.from(table)
        .map(pollingRateLevelToHz)
        .filter((hz) => hz !== null)
    )
  ).sort((a, b) => a - b);
};
